<template>
  <div class="archive">
    <SearchBar @search="searchHandler" :loading="archive.loading" />
    <div class="table-wrap" ref="tableWrap">
      <a-table class="archive-table" rowKey="_id" :columns="columns" :loading="archive.loading"
        :data-source="archive.value.records" :pagination="pagination" :searchOptions="searchOptions"
        @change="changeHandler" :scroll="{ x: tableMinWidth, y: tableScrollY }">
        <div class="cell" slot="dateCreated" slot-scope="text">
          {{ beautifyDate(text) }}
        </div>

        <span slot="name" slot-scope="text" class="filename">{{ text }}</span>

        <template slot="generation" slot-scope="text, record">
          <a-tooltip v-if="record.provenance" :overlayStyle="{ maxWidth: '380px' }">
            <template slot="title">
              <div class="tip">
                <div class="tip-head">{{ record.provenance.stamped ? 'Stamped by the pipeline' : 'Inferred from the model bytes' }}</div>
                <div v-if="!record.provenance.stamped" class="tip-sub">converted before provenance tracking</div>
                <div class="tip-row"><span class="tip-label">Generation</span><span>{{ generationLabel(record.provenance.generation) }}</span></div>
                <div class="tip-row"><span class="tip-label">Type fidelity</span><span>{{ fidelityLabel(record.provenance.schema) }}</span></div>
                <div class="tip-row" v-for="[component, version] in pipelineEntries(record.provenance.pipeline)" :key="component">
                  <span class="tip-label">{{ component }}</span><span>{{ version }}</span>
                </div>
                <div v-if="!pipelineEntries(record.provenance.pipeline).length" class="tip-row"><span class="tip-label">Pipeline</span><span>unstamped</span></div>
              </div>
            </template>
            <a-tag :color="generationColor(record.provenance)">
              {{ text }}<span v-if="!record.provenance.stamped"> · inferred</span>
            </a-tag>
          </a-tooltip>
          <span v-else class="muted">—</span>
        </template>

        <template slot="requiredEngine" slot-scope="text, record">
          <span v-if="record.provenance">{{ text || 'unknown' }}</span>
          <span v-else class="muted">—</span>
        </template>

        <template slot="source" slot-scope="text, record">
          <a-tooltip v-if="text" :title="`${record.provenance.source.filename} — click to copy the full hash`">
            <a class="hash" @click="copyText(text)">{{ text.slice(0, 10) }}…</a>
          </a-tooltip>
          <span v-else class="muted">—</span>
        </template>

        <template slot="actions" slot-scope="__, record">
          <div class="action-cell">
            <a-tooltip title="Compare with Excel">
              <a-button class="action-btn" type="link" @click="openCheckModal(record)">
                <a-icon type="file-excel" style="font-size: large;" />
              </a-button>
            </a-tooltip>
          </div>
        </template>

        <div slot="expandedRowRender" slot-scope="record" style="margin: 0">
          <dl v-if="record.provenance" class="provenance-details">
            <dt>Converted</dt><dd>{{ record.provenance.artifact.convertedAt ? beautifyDate(record.provenance.artifact.convertedAt) : 'before provenance tracking (row inferred from the bytes)' }}</dd>
            <dt>Pipeline</dt><dd>{{ pipelineText(record.provenance.pipeline) }}</dd>
            <dt>Schema</dt><dd>{{ schemaText(record.provenance.schema) }}</dd>
            <dt>Content hash</dt><dd><a class="hash" @click="copyText(record.provenance.artifact.contentSha256)">{{ record.provenance.artifact.contentSha256 }}</a></dd>
            <dt>Model bytes</dt><dd><a class="hash" @click="copyText(record.provenance.artifact.sha256)">{{ record.provenance.artifact.sha256 }}</a></dd>
            <template v-if="record.provenance.inconsistencies && record.provenance.inconsistencies.length">
              <dt>Inconsistent</dt><dd class="inconsistent">{{ record.provenance.inconsistencies.join('; ') }}</dd>
            </template>
          </dl>
          <a-list item-layout="horizontal" :data-source="record.files">
            <a-list-item slot="renderItem" slot-scope="item">
              <a-list-item-meta>
                <a slot="title" @click="getFile(`${record._id}/${item.path}`, item.name, record.ownerId._id)">{{
                  item.name
                  }}</a>
                <a-avatar slot="avatar" icon="file" />
              </a-list-item-meta>
            </a-list-item>
          </a-list>
        </div>
      </a-table>
    </div>

    <a-modal :visible="checkModal.visible" title="Compare with Excel" :confirm-loading="checkModal.loading"
      @ok="runCheck" @cancel="closeCheckModal" okText="Run">
      <div style="display:grid;gap:12px">
        <a-alert type="info" show-icon message="Check calculation chain mismatches"
          :description="`The script calculates the entire dependency chain for the target cell and compares each dependent cell with its corresponding value in Excel. If mismatches are found, they are reported in the order they occur; otherwise, the target value is returned.`" />

        <div>
          <label class="field-label">Target mapping</label>
          <a-input v-model="checkModal.mapping" placeholder="Enter mapping, e.g. Sheet1!A2" />
        </div>

        <div class="param-grid">
          <div class="field-label">
            <span>Accuracy</span>
            <a-tooltip placement="top" title="Acceptable deviation between the target value and the computed value.">
              <a-icon type="info-circle" class="field-help" />
            </a-tooltip>
          </div>

          <div class="field-label">
            <span>Max mismatches</span>
            <a-tooltip placement="top" title="Maximum number of mismatched cells returned">
              <a-icon type="info-circle" class="field-help" />
            </a-tooltip>
          </div>

          <a-input-number v-model="checkModal.accuracy" size="small" :min="0" :step="0.0001" :precision="4"
            :controls="false" placeholder="0.0000" class="param-input" @focus="selectAll" />

          <a-input-number v-model="checkModal.maxMismatches" size="small" :min="-1" :step="1" :controls="false"
            placeholder="1000" class="param-input" @focus="selectAll" />
        </div>

        <a-alert v-if="checkModal.summary" :type="alertType" show-icon :message="checkModal.summary.text" />

        <a-table v-if="checkModal.mismatches.length" :data-source="checkModal.mismatches" :columns="mismatchColumns"
          rowKey="cellRef" size="small" :pagination="false" :scroll="{ y: 260 }" />

        <a-alert v-if="checkModal.error" type="error" show-icon :message="checkModal.error" />
      </div>
    </a-modal>
  </div>
</template>

<script>
import SearchBar from "../components/search_bar.vue";
import { mapActions, mapState } from "vuex";
import moment from "moment";

// Explicit widths + horizontal scroll (TABLE_MIN_WIDTH): the table keeps readable
// columns on any viewport instead of squeezing every cell into wrapped fragments
const baseColumns = [
  { title: "File Name", dataIndex: "name", sorter: true, width: 260, scopedSlots: { customRender: "name" } },
  { title: "Account", dataIndex: "ownerId.username", sorter: true, width: 120 },
  {
    title: "Date Created",
    dataIndex: "dateCreated",
    scopedSlots: { customRender: "dateCreated" },
    sorter: true,
    width: 150,
    className: "nowrap",
  },
  { title: "Status", dataIndex: "status", sorter: true, width: 120, className: "nowrap" },
  { title: "Company", dataIndex: "builderMeta.company", sorter: true, width: 140 },
  { title: "Presentation", dataIndex: "builderMeta.presentation", sorter: true, width: 150 },
  { title: "Workspace", dataIndex: "builderMeta.workspace", sorter: true, width: 140 },
  { title: "User", dataIndex: "builderMeta.user", sorter: true, width: 120 },
  { title: "Engine", dataIndex: "extractorMeta.engineVersion", width: 130, className: "nowrap" },
  // Provenance ledger (engine agent_docs/model_provenance.md): generation of the
  // wire format, the engine the model needs, and the source workbook's content hash
  { title: "Generation", dataIndex: "provenance.generation", scopedSlots: { customRender: "generation" }, width: 120 },
  { title: "Engine ≥", dataIndex: "provenance.requiredEngine", scopedSlots: { customRender: "requiredEngine" }, width: 100 },
  { title: "Source", dataIndex: "provenance.source.sha256", scopedSlots: { customRender: "source" }, width: 130 },
  { title: "Actions", key: "actions", scopedSlots: { customRender: "actions" }, width: 90 },
];
const TABLE_MIN_WIDTH = baseColumns.reduce((sum, column) => sum + (column.width || 120), 60);

export default {
  components: { SearchBar },

  data() {
    return {
      pagination: {
        total: 1,
        current: 0,
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
      },
      searchOptions: {
        fileName: "",
        account: "",
        dateCreated: "",
        status: "",
        company: "",
        presentation: "",
        workspace: "",
        user: "",
        generation: "",
        stamped: "",
        sourceSha256: "",
      },
      tableConfig: {
        sortBy: "dateCreated",
        sortOrder: "desc",
        pageSize: 10,
        page: 0,
      },
      checkModal: {
        visible: false,
        loading: false,
        processId: null,
        mapping: "",
        xlsxPath: "",
        xlsxLabel: "",
        accuracy: 0.0001,
        maxMismatches: 1000,
        mismatches: [],
        targetValRes: null,
        error: "",
        summary: null,
      },
      mismatchColumns: [
        { title: "Cell", dataIndex: "cellRef" },
        { title: "Engine", dataIndex: "engineValue" },
        { title: "Excel", dataIndex: "thirdPartyValue" },
      ],
      tableScrollY: 400,
      tableMinWidth: TABLE_MIN_WIDTH,
      _ro: null,
    };
  },

  computed: {
    ...mapState("archive", ["archive"]),
    ...mapState("auth", ["userRole"]),
    columns() {
      return this.userRole == "USER"
        ? baseColumns.filter(c => c.dataIndex != "ownerId")
        : baseColumns;
    },
    alertType() {
      const s = this.checkModal.summary;
      if (!s) return null;

      const mismatches =
        s.mismatchesCount ??
        (Array.isArray(this.checkModal.mismatches)
          ? this.checkModal.mismatches.length
          : 0);

      if (!s.ok) return "error";
      if (mismatches > 0) return "warning";
      return "success";
    },
  },

  methods: {
    ...mapActions("archive", ["fetchFiles"]),
    async getFile(path, name, userId) {
      const { data } = await this.$http.get("/api/process/download", {
        params: { path, userId },
        responseType: "blob",
        headers: { Authorization: this.$store.state.auth.token },
      });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    },
    async defaultFetchFiles(startPage) {
      return this.fetchFiles({
        sortBy: this.tableConfig.sortBy,
        sortOrder: this.tableConfig.sortOrder,
        page: startPage || this.tableConfig.page,
        pageSize: this.tableConfig.pageSize,
        filters: { ...this.searchOptions },
      });
    },
    async changeHandler(pagination, _filters, sorter) {
      this.tableConfig.sortBy = sorter.field;
      this.tableConfig.sortOrder = sorter.order == "ascend" ? "asc" : "desc";
      this.tableConfig.page = pagination.current - 1;
      this.tableConfig.pageSize = pagination.pageSize;
      this.pagination.current = pagination.current;

      this.defaultFetchFiles();
    },

    async searchHandler(formState) {
      this.searchOptions = { ...formState };
      this.pagination.current = 0;
      this.tableConfig.page = 0;

      const { total } = await this.defaultFetchFiles(0);
      this.pagination.total = total;
    },

    beautifyDate(date) {
      return moment(date).format("HH:mm DD/MM/YYYY");
    },

    generationColor(provenance) {
      if (provenance.inconsistencies && provenance.inconsistencies.length) return "red";
      // v4 (the current wire format) in the brand indigo; an unknown generation
      // gets a dark neutral — antd's "default" tag is too faint to read
      return { v0: "orange", v1: "orange", v2: "blue", v3: "green", v4: "#222377" }[provenance.generation] || "#595959";
    },
    pipelineText(pipeline) {
      const entries = Object.entries(pipeline || {});
      return entries.length ? entries.map(([k, v]) => `${k} ${v}`).join(" · ") : "unstamped";
    },
    schemaText(schema) {
      return Object.entries(schema || {}).map(([k, v]) => `${k} ${v === null ? '?' : v}`).join(" · ");
    },
    generationLabel(generation) {
      return { v0: "v0 · 2-slot metadata", v1: "v1 · stringly literals", v2: "v2 · typed literals", v3: "v3 · faithful types", v4: "v4 · reference range bounds" }[generation] || generation || "unknown";
    },
    fidelityLabel(schema) {
      const fidelity = schema && schema.typeFidelity;
      if (fidelity === 1) return "1 · booleans, errors and text kept as-is";
      if (fidelity === 0) return "0 · legacy typing (booleans as 1/0, errors blank)";
      return "unknown";
    },
    pipelineEntries(pipeline) {
      return Object.entries(pipeline || {});
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success("Copied");
      } catch (e) {
        this.$message.info(text);
      }
    },

    openCheckModal(record) {
      const excelFile = (record.files || []).find(f => /\.(xlsx|xlsm)$/i.test(f.name || f.path));

      this.checkModal.visible = true;
      this.checkModal.loading = false;
      this.checkModal.processId = record._id;
      this.checkModal.mapping = "";

      if (excelFile) {
        this.checkModal.xlsxPath = `${record._id}/${excelFile.path}`;
        this.checkModal.xlsxLabel = excelFile.name || excelFile.path;
        this.checkModal.error = "";
      } else {
        this.checkModal.xlsxPath = "";
        this.checkModal.xlsxLabel = "";
        this.checkModal.error = "No .xlsx or .xlsm file found in this archive item.";
      }

      this.checkModal.mismatches = [];
      this.checkModal.targetValRes = null;
      this.checkModal.summary = null;
      this.checkModal.accuracy = 0.0001;
      this.checkModal.maxMismatches = 1000;
    },

    closeCheckModal() {
      this.checkModal.visible = false;
      this.checkModal.loading = false;
      this.checkModal.mismatches = [];
      this.checkModal.error = "";
      this.checkModal.summary = null;
    },

    async runCheck() {
      const { processId, mapping, xlsxPath, accuracy, maxMismatches } = this.checkModal;

      if (!processId) { this.checkModal.error = "Missing processId"; return; }
      if (!mapping) { this.checkModal.error = "Please enter a mapping (e.g. Sheet1!A2)"; return; }
      if (!xlsxPath) { this.checkModal.error = "Please select an .xlsx or .xlsm file"; return; }

      this.checkModal.loading = true;
      this.checkModal.mismatches = [];
      this.checkModal.error = "";
      this.checkModal.summary = null;

      try {
        const payload = {
          file: {
            processId,
            xlsxPath,
            target: mapping,
            tolerance: Number(accuracy) || 0.0001,
            maxMismatches: Number(maxMismatches) || 1000,
          },
        };

        const { data } = await this.$http.post(
          "/api/process/checkCalcChainScript",
          payload,
          { headers: { Authorization: this.$store.state.auth.token } }
        );

        const result = data?.results?.[0];
        if (!result) { this.checkModal.error = "No result returned from API."; return; }

        this.checkModal.mismatches = Array.isArray(result.mismatches) ? result.mismatches : [];
        this.checkModal.targetValRes = result.targetValRes;

        const count = result.mismatchesCount ?? this.checkModal.mismatches.length;
        const targetValPart =
          (result.targetValRes !== null && result.targetValRes !== undefined)
            ? ` · Target value: ${result.targetValRes}`
            : "";

        const base = `Target: ${result.target || mapping} · ${count} mismatches · ${result.tookMs ?? "—"} ms${targetValPart}`;
        this.checkModal.summary = result.error
          ? { ok: false, text: `${base} · Error: ${result.error}` }
          : { ok: !!result.ok, text: base };
      } catch (e) {
        this.checkModal.error = String(e?.response?.data?.error || e?.message || e);
      } finally {
        this.checkModal.loading = false;
      }
    },

    selectAll(e) {
      if (e?.target && typeof e.target.select === "function") {
        requestAnimationFrame(() => e.target.select());
      }
    },

    recomputeScrollY() {
      this.$nextTick(() => {
        const wrap = this.$refs.tableWrap;
        if (!wrap) return;

        const wrapH = wrap.clientHeight;
        const table = wrap.querySelector(".ant-table-wrapper") || wrap;
        const header = table.querySelector(".ant-table-header");
        const pagination = table.querySelector(".ant-table-pagination, .ant-pagination");

        const headerH = header ? header.offsetHeight : 0;
        const pagH = pagination ? pagination.offsetHeight : 0;
        const paddings = 16;

        this.tableScrollY = Math.max(160, wrapH - headerH - pagH - paddings);
      });
    },
  },

  async mounted() {
    const { total } = await this.fetchFiles();
    this.$set(this.pagination, "total", total);

    this.$nextTick(() => this.recomputeScrollY());

    const wrap = this.$refs.tableWrap;
    if (window.ResizeObserver && wrap) {
      this._ro = new ResizeObserver(() => this.recomputeScrollY());
      this._ro.observe(wrap);
    }
    window.addEventListener("resize", this.recomputeScrollY);

    this.$watch(
      () => [this.archive.loading, this.pagination, this.searchOptions],
      () => this.recomputeScrollY(),
      { deep: true }
    );
  },

  beforeDestroy() {
    if (this._ro) this._ro.disconnect();
    window.removeEventListener("resize", this.recomputeScrollY);
  },
};
</script>

<style scoped>
::v-deep .ant-table-tbody>tr .action-cell .action-btn {
  opacity: 0;
  transition: opacity .15s ease-in-out;
}

::v-deep .ant-table-tbody>tr:hover .action-cell .action-btn {
  opacity: 1;
}

::v-deep(.ant-tooltip-inner) {
  font-size: 11px;
  padding: 2px 6px;
}

.tip {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 2px;
  text-align: left;
  font-size: 12px;
  line-height: 1.4;
}
.tip-head { font-weight: 700; }
.tip-sub { color: rgba(255, 255, 255, 0.7); margin-bottom: 4px; }
.tip-row { display: grid; grid-template-columns: 96px 1fr; gap: 8px; }
.tip-label { color: rgba(255, 255, 255, 0.7); text-transform: capitalize; }

::v-deep(.archive-table .ant-table-thead > tr > th),
::v-deep(.archive-table .ant-table-tbody > tr > td) {
  text-align: center !important;
  vertical-align: middle;
}

.filename {
  display: inline-block;
  text-align: left;
  word-break: normal;
  overflow-wrap: anywhere;
}

::v-deep(.archive-table .nowrap) {
  white-space: nowrap;
}

::v-deep(.ant-list-item-meta-content) {
  text-align: left;
}

.provenance-details {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 4px 12px;
  text-align: left;
  font-size: 12px;
  margin: 0 0 12px;
}
.provenance-details dt { color: rgba(0, 0, 0, 0.5); }
.provenance-details dd { margin: 0; word-break: break-all; }
.provenance-details .inconsistent { color: #cf1322; }
.hash { font-family: "SFMono-Regular", Menlo, Consolas, monospace; font-size: 12px; }
.muted { color: rgba(0, 0, 0, 0.3); }

/* The view fills the layout's content area; the table takes whatever height is
   left under the search bar / filter panel and scrolls inside (recomputeScrollY) */
.archive {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

::v-deep .ant-table-wrapper {
  flex: 1;
  min-height: 0;
}

.param-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: auto;
  column-gap: 16px;
  row-gap: 6px;
  margin-top: 8px;
}

.field-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.85);
  padding: 5px 2px;
}

.field-help {
  color: #1890ff;
  opacity: 0.9;
  cursor: help;
}

.param-input {
  width: 100%;
}

@media (max-width: 640px) {
  .param-grid {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
}
</style>
